import React, { useState } from 'react';
import { Sparkles, Gem, Wand2, Check } from 'lucide-react';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Switch } from '../ui/switch';
import { conciergePresets } from '../../mock';
import { useAuth } from '../../context/AuthContext';
import { API } from '../../lib/api';
import { toast } from 'sonner';

export default function ConciergeTab() {
  const { user, setUser } = useAuth();
  const isPro = user?.plan === 'pro';
  const [preset, setPreset] = useState(user?.concierge?.preset || 'objection');
  const [prompt, setPrompt] = useState(user?.concierge?.system_prompt || conciergePresets[0].prompt);
  const [enabled, setEnabled] = useState(user?.concierge?.enabled ?? true);
  const [leadCapture, setLeadCapture] = useState(true);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await API.put('/creator/settings', { concierge: { preset, system_prompt: prompt, enabled } });
      setUser(data);
      toast.success('AI Concierge settings saved');
    } catch { toast.error('Could not save'); }
    finally { setSaving(false); }
  };

  const applyPreset = (p) => {
    setPreset(p.id);
    setPrompt(p.prompt);
    toast.success(`${p.name} template loaded`);
  };

  return (
    <div className="space-y-6">
      <div className="glass flex items-center gap-4 rounded-2xl p-5">
        <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary/15">
          <Sparkles className="h-5 w-5 text-primary" />
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <p className="font-display text-sm font-semibold text-white">AI Sales Concierge</p>
            <span className="flex items-center gap-1 rounded-full bg-primary/15 px-2 py-0.5 text-[10px] font-semibold text-primary"><Gem className="h-3 w-3" /> PRO</span>
          </div>
          <p className="text-xs text-muted-foreground">Answers buyer questions & handles objections on your storefront.</p>
        </div>
        <Switch checked={enabled} onCheckedChange={setEnabled} className="data-[state=checked]:bg-primary" />
      </div>

      {/* Template preset selector */}
      <div className="glass rounded-2xl p-5">
        <div className="mb-3 flex items-center gap-2">
          <Wand2 className="h-4 w-4 text-primary" />
          <h3 className="font-display text-sm font-semibold text-white">Template Preset</h3>
        </div>
        <div className="grid gap-3 sm:grid-cols-3">
          {conciergePresets.map((p) => (
            <button
              key={p.id}
              onClick={() => applyPreset(p)}
              className={`rounded-xl border p-4 text-left transition-luxe ${preset === p.id ? 'border-primary/50 bg-primary/[0.08]' : 'border-white/10 bg-white/[0.03] hover:border-white/20'}`}
            >
              <div className="flex items-center justify-between">
                <span className="font-display text-sm font-semibold text-white">{p.name}</span>
                {preset === p.id && <Check className="h-4 w-4 text-primary" />}
              </div>
              <p className="mt-1 text-xs text-muted-foreground">{p.description}</p>
            </button>
          ))}
        </div>
      </div>

      {/* System prompt editor */}
      <div className="glass rounded-2xl p-5">
        <div className="mb-2 flex items-center justify-between">
          <h3 className="font-display text-sm font-semibold text-white">System Prompt</h3>
          <span className="text-xs text-muted-foreground">Edit placeholders like [Product Name], [Price], [Key Benefit]</span>
        </div>
        <Textarea
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          rows={9}
          className="rounded-xl border-white/10 bg-white/[0.04] font-mono text-sm leading-relaxed text-white/90 focus-visible:ring-primary"
        />
        <div className="mt-3 flex flex-wrap gap-2">
          {['[Creator Name]', '[Product Name]', '[Price]', '[Key Benefit]'].map((ph) => (
            <button key={ph} onClick={() => setPrompt((v) => v + ' ' + ph)} className="rounded-full border border-white/10 bg-white/[0.03] px-2.5 py-1 text-xs text-primary hover:bg-primary/10">
              {ph}
            </button>
          ))}
        </div>
      </div>

      {/* Lead capture toggle */}
      <div className="glass flex items-center gap-4 rounded-2xl p-5">
        <div className="flex-1">
          <p className="font-display text-sm font-semibold text-white">In-conversation lead capture</p>
          <p className="text-xs text-muted-foreground">Non-blocking email ask inside the chat & quiz results.</p>
        </div>
        <Switch checked={leadCapture} onCheckedChange={setLeadCapture} className="data-[state=checked]:bg-primary" />
      </div>

      <Button onClick={save} disabled={saving} className="w-full rounded-xl bg-primary font-semibold text-primary-foreground hover:bg-primary/90 glow-lime">
        {saving ? 'Saving…' : 'Save configuration'}
      </Button>
    </div>
  );
}
